// 📄 pages/Proyectos/OrganigramaProyecto.tsx
// "Organigrama" — vista de 2 columnas (equipo de la consultora vs equipo
// del cliente) con el cargo de cada persona y sus responsabilidades/
// accesos, más un indicador de cargo "sin cubrir" cuando ningún miembro
// activo lo tiene asignado. Reusa `project.miembros` y el `rol` real que
// ya existe (rol === 'cliente' define de qué lado va cada persona) — solo
// agrega 2 campos puramente descriptivos (cargoOrganigrama/
// responsabilidades) que NO tocan ningún permiso, ver projectService.ts.
import React, { useState } from 'react';
import { Network, Pencil, UserX } from 'lucide-react';
import { useProject } from '../../context/ProjectContext.tsx';
import { ProjectMember, ROLE_OPTIONS } from '../../services/projectService.ts';
import OrganigramaMiembroModal from './components/OrganigramaMiembroModal.tsx';

const CARGOS_CONSULTORA = ['PM', 'Equipo Funcional', 'Equipo Técnico', 'Soporte'];
const CARGOS_CLIENTE = ['PM', 'Equipo Funcional', 'Equipo Técnico', 'Basis'];

const ROL_LABEL: Record<string, string> = Object.fromEntries(ROLE_OPTIONS.map((r) => [r.value, r.label]));

function MiembroCard({ miembro, puedeEditar, onEditar }: { miembro: ProjectMember; puedeEditar: boolean; onEditar: () => void }) {
  return (
    <div className="bg-white/5 border border-white/10 rounded-xl p-3 space-y-1.5">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="text-sm text-gray-200 truncate">{miembro.email}</p>
          <span className="text-[10px] text-gray-500 uppercase tracking-wider">{ROL_LABEL[miembro.rol] || miembro.rol}</span>
        </div>
        {puedeEditar && (
          <button onClick={onEditar} className="text-gray-500 hover:text-white flex-shrink-0">
            <Pencil className="w-3.5 h-3.5" />
          </button>
        )}
      </div>
      {miembro.responsabilidades && <p className="text-xs text-gray-400 whitespace-pre-wrap">{miembro.responsabilidades}</p>}
    </div>
  );
}

function CargoVacio({ cargo }: { cargo: string }) {
  return (
    <div className="border border-dashed border-white/10 rounded-xl p-3 flex items-center gap-2 text-xs text-gray-600">
      <UserX className="w-3.5 h-3.5 flex-shrink-0" />
      Sin nadie asignado a {cargo}
    </div>
  );
}

function Columna({
  titulo, miembros, cargos, puedeEditar, onEditarMiembro
}: {
  titulo: string; miembros: ProjectMember[]; cargos: string[]; puedeEditar: boolean; onEditarMiembro: (m: ProjectMember) => void;
}) {
  const activos = miembros.filter((m) => m.activo !== false);
  const sinCargo = activos.filter((m) => !m.cargoOrganigrama || !cargos.includes(m.cargoOrganigrama));

  return (
    <div className="flex-1 min-w-0 space-y-4">
      <h3 className="text-sm font-bold text-white uppercase tracking-wider">{titulo}</h3>

      {cargos.map((cargo) => {
        const deEsteCargo = activos.filter((m) => m.cargoOrganigrama === cargo);
        return (
          <div key={cargo} className="space-y-2">
            <p className="text-xs font-semibold text-blue-400">{cargo}</p>
            {deEsteCargo.length === 0
              ? <CargoVacio cargo={cargo} />
              : deEsteCargo.map((m) => <MiembroCard key={m.userId} miembro={m} puedeEditar={puedeEditar} onEditar={() => onEditarMiembro(m)} />)}
          </div>
        );
      })}

      {sinCargo.length > 0 && (
        <div className="space-y-2 pt-2 border-t border-white/10">
          <p className="text-xs font-semibold text-gray-500">Sin cargo asignado todavía</p>
          {sinCargo.map((m) => <MiembroCard key={m.userId} miembro={m} puedeEditar={puedeEditar} onEditar={() => onEditarMiembro(m)} />)}
        </div>
      )}
    </div>
  );
}

export default function OrganigramaProyecto() {
  const { project, projectId, esCliente, esAdminObservador } = useProject();
  const [editando, setEditando] = useState<ProjectMember | null>(null);

  const puedeEditar = !esCliente && !esAdminObservador;

  if (!project) return null;

  const miembros = project.miembros || [];
  const equipoConsultora = miembros.filter((m) => m.rol !== 'cliente');
  const equipoCliente = miembros.filter((m) => m.rol === 'cliente');

  return (
    <div className="space-y-6">
      <h1 className="text-xl font-semibold text-white flex items-center gap-2">
        <Network className="w-5 h-5 text-blue-400" /> Organigrama del Proyecto
      </h1>
      <p className="text-sm text-gray-400 -mt-4">
        Quién forma parte del proyecto de cada lado, su cargo y sus responsabilidades/accesos. El cargo acá es solo informativo — no cambia lo que cada persona puede hacer en la plataforma (eso lo sigue definiendo su rol, en "Miembros").
      </p>

      <div className="flex flex-col lg:flex-row gap-8">
        <Columna
          titulo="Nuestro equipo"
          miembros={equipoConsultora}
          cargos={CARGOS_CONSULTORA}
          puedeEditar={puedeEditar}
          onEditarMiembro={setEditando}
        />
        <div className="hidden lg:block w-px bg-white/10" />
        <Columna
          titulo={`Equipo de ${project.clienteNombre || 'cliente'}`}
          miembros={equipoCliente}
          cargos={CARGOS_CLIENTE}
          puedeEditar={puedeEditar}
          onEditarMiembro={setEditando}
        />
      </div>

      {editando && (
        <OrganigramaMiembroModal
          projectId={projectId}
          miembro={editando}
          cargosDisponibles={editando.rol === 'cliente' ? CARGOS_CLIENTE : CARGOS_CONSULTORA}
          onClose={() => setEditando(null)}
          onSaved={() => setEditando(null)}
        />
      )}
    </div>
  );
}
